import { useEffect, useState } from "react";
import {
  ImageKind,
  OrganizationBookingSettingsResponse,
  WebPageService,
} from "client/openapi";
import {
  COMPACT_CHECK,
  COMPACT_INPUT,
  COMPACT_TEXTAREA,
  CompactRadio,
} from "components/Inputs/compact";
import { Switch } from "components/Switch";
import CopyLinkRow from "components/PublicBooking/CopyLinkRow";
import { widgetCode } from "util/embed";
import ImageUpload from "components/PublicBooking/ImageUpload";
import { OrgLogo } from "components/PublicBooking/PublicShell";
import { LABEL, SECTION, SECTION_TEXT, SECTION_TITLE } from "./settingsStyles";

/**
 * What the tab saves: the whole tab in one request. The logo saves through
 * upload and Remove only.
 */
type Settings = {
  enabled: boolean;
  title: string;
  bio: string;
  primary_color: string;
  families_choose_duration: boolean;
  duration_options: number[];
  default_duration: number;
  families_choose_location: boolean;
  default_location_id: number | null;
  min_notice_minutes: number;
  max_days_ahead: number | null;
  buffer_minutes: number;
  returning_families_only: boolean;
  shown_subject_ids: number[];
  offered_location_ids: number[];
};

function editable(loaded: OrganizationBookingSettingsResponse): Settings {
  return {
    enabled: loaded.enabled,
    title: loaded.title ?? "",
    bio: loaded.bio ?? "",
    primary_color: loaded.primary_color,
    families_choose_duration: !!loaded.families_choose_duration,
    duration_options: loaded.duration_options ?? [60],
    default_duration: loaded.default_duration ?? 60,
    families_choose_location: !!loaded.families_choose_location,
    default_location_id: loaded.default_location_id ?? null,
    min_notice_minutes: loaded.min_notice_minutes ?? 0,
    max_days_ahead: loaded.max_days_ahead ?? null,
    buffer_minutes: loaded.buffer_minutes ?? 0,
    returning_families_only: !!loaded.returning_families_only,
    shown_subject_ids: loaded.subjects
      .filter((s) => s.show_on_public_booking_page)
      .map((s) => s.id),
    offered_location_ids: loaded.locations
      .filter((l) => l.show_on_booking_page)
      .map((l) => l.id),
  };
}

/** The same settings, whatever order their lists are in. */
const same = (a: unknown, b: unknown) =>
  JSON.stringify(a, sortLists) === JSON.stringify(b, sortLists);

function sortLists(_key: string, value: unknown) {
  return Array.isArray(value) ? [...value].sort((x, y) => x - y) : value;
}

/**
 * `edited` was made from `before`; its edits carried onto `after`, the tab as
 * reloaded. A setting not edited takes the reloaded value. The subject and
 * place lists keep what was ticked or unticked here and take the rest, such
 * as a subject added in the Subjects tab, from the reload. A place deleted in
 * the Locations tab meanwhile is dropped, so Save never sends it.
 */
function rebase(
  edited: Settings,
  before: Settings,
  after: Settings,
  placeIds: number[]
): Settings {
  const kept = Object.fromEntries(
    Object.entries(edited).filter(
      ([key, value]) => !same(value, before[key as keyof Settings])
    )
  );
  if (
    kept.default_location_id != null &&
    !placeIds.includes(kept.default_location_id as number)
  ) {
    delete kept.default_location_id;
  }
  const merge = (key: "shown_subject_ids" | "offered_location_ids") => {
    const ticked = edited[key].filter(
      (id) =>
        !before[key].includes(id) &&
        (key !== "offered_location_ids" || placeIds.includes(id))
    );
    const unticked = before[key].filter((id) => !edited[key].includes(id));
    return [
      ...after[key].filter(
        (id) => !unticked.includes(id) && !ticked.includes(id)
      ),
      ...ticked,
    ];
  };
  return {
    ...after,
    ...kept,
    shown_subject_ids: merge("shown_subject_ids"),
    offered_location_ids: merge("offered_location_ids"),
  };
}

/** "No minimum", "4 hours", "2 days", "1 week". */
function noticeLabel(minutes: number) {
  if (minutes === 0) return "No minimum";
  if (minutes % 10080 === 0) return plural(minutes / 10080, "week");
  if (minutes % 1440 === 0 && minutes > 1440)
    return plural(minutes / 1440, "day");
  return plural(minutes / 60, "hour");
}

/** "2 weeks", "1 month", "6 months" for a booking window in days. */
function windowLabel(days: number) {
  return days % 30 === 0
    ? plural(days / 30, "month")
    : plural(days / 7, "week");
}

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? "" : "s"}`;

/**
 * The Booking pages tab as saved and as edited, for the settings dialog's
 * Save and Cancel. It loads each time the tab is opened: the Subjects and
 * Locations tabs may have added subjects or removed places since. Edits made
 * before that carry over.
 */
export function useBookingPages(orgId: number, active: boolean) {
  const [loaded, setLoaded] = useState<OrganizationBookingSettingsResponse>();
  const [form, setForm] = useState<{ saved: Settings; settings: Settings }>();
  const [logo, setLogo] = useState<string | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!active) return;
    let current = true;
    WebPageService.getOrganizationBookingSettings({ orgId })
      .then((response) => {
        if (!current) return;
        const fresh = editable(response);
        setLoaded(response);
        setLogo(response.photo_url ?? null);
        setForm((before) => ({
          saved: fresh,
          settings: before
            ? rebase(
                before.settings,
                before.saved,
                fresh,
                response.locations.map((location) => location.id)
              )
            : fresh,
        }));
      })
      .catch(() => current && setLoadFailed(true));
    return () => {
      current = false;
    };
  }, [orgId, active]);

  return {
    loaded,
    settings: form?.settings,
    logo,
    setLogo,
    loadFailed,
    set: (changes: Partial<Settings>) =>
      setForm(
        (current) =>
          current && {
            ...current,
            settings: { ...current.settings, ...changes },
          }
      ),
    changed: !!form && !same(form.settings, form.saved),
    /** Sends the whole tab in one request. */
    save: async () => {
      if (!form) return;
      const sent = form.settings;
      await WebPageService.updateOrganizationWebPageByOrgId({
        orgId,
        requestBody: sent,
      });
      setForm((current) => current && { ...current, saved: sent });
    },
    /** Starts over, as if the tab had never been opened. */
    reset: () => {
      setLoaded(undefined);
      setForm(undefined);
      setLogo(null);
      setLoadFailed(false);
    },
  };
}

export type BookingPagesForm = ReturnType<typeof useBookingPages>;

/**
 * Organization Settings > Booking pages: whether families can book online,
 * what the page shows, and the booking rules. The dialog's Save saves them.
 */
export default function BookingPagesSettings({
  orgId,
  bookingPages,
}: {
  orgId: number;
  bookingPages: BookingPagesForm;
}) {
  const { loaded, settings, logo, setLogo, loadFailed, set } = bookingPages;

  if (loadFailed)
    return (
      <p className="text-sm text-red-600">
        The booking settings could not be loaded.
      </p>
    );
  if (!loaded || !settings) return null;

  const { choices } = loaded;
  const subjects = loaded.subjects.filter((s) => s.active);
  const places = loaded.locations;
  const base = loaded.custom_domain
    ? `https://${loaded.custom_domain}`
    : `${process.env.REACT_APP_FRONTEND_DOMAIN}/organizations/${orgId}`;

  const toggleLength = (minutes: number) => {
    const ticked = settings.duration_options.includes(minutes);
    if (ticked && settings.duration_options.length === 1) return;
    const options = ticked
      ? settings.duration_options.filter((m) => m !== minutes)
      : [...settings.duration_options, minutes].sort((a, b) => a - b);
    set({
      duration_options: options,
      default_duration: options.includes(settings.default_duration)
        ? settings.default_duration
        : options[0],
    });
  };

  const togglePlace = (id: number) => {
    const ticked = settings.offered_location_ids.includes(id);
    if (ticked && settings.offered_location_ids.length === 1) return;
    set({
      offered_location_ids: ticked
        ? settings.offered_location_ids.filter((p) => p !== id)
        : [...settings.offered_location_ids, id],
    });
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid items-start gap-4 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <section className={SECTION}>
            <Switch
              name="online-booking"
              checked={settings.enabled}
              onCheckedChange={(on) => set({ enabled: on })}
            >
              <label
                htmlFor="online-booking"
                className={`${SECTION_TITLE} cursor-pointer`}
              >
                Online booking
              </label>
            </Switch>
            <p className={SECTION_TEXT}>
              Families can book tutors from your public pages.
            </p>
            {settings.enabled && (
              <div className="mt-2 flex flex-col gap-1.5">
                <CopyLinkRow
                  url={base}
                  embed={() =>
                    widgetCode(
                      `/organizations/${orgId}`,
                      `Book a tutor with ${loaded.org_name}`
                    )
                  }
                />
                <CopyLinkRow
                  url={`${base}/classes`}
                  embed={() =>
                    widgetCode(
                      `/organizations/${orgId}/classes`,
                      `Classes at ${loaded.org_name}`
                    )
                  }
                />
              </div>
            )}
          </section>

          <section className={SECTION}>
            <h3 className={SECTION_TITLE}>Your page</h3>
            <p className={SECTION_TEXT}>
              Shown at the top of your booking page.
            </p>
            <ImageUpload
              orgId={orgId}
              kind={ImageKind.ORG_LOGO}
              hasImage={!!logo}
              noun="logo"
              label={<p className={`${LABEL} mb-1.5`}>Logo</p>}
              className="mt-4 flex items-center gap-4"
              onUploaded={setLogo}
              onRemove={() =>
                WebPageService.updateOrganizationWebPageByOrgId({
                  orgId,
                  requestBody: { photo_url: null },
                }).then(() => setLogo(null))
              }
            >
              <OrgLogo
                url={logo}
                name={loaded.org_name}
                size={88}
                maxWidth={176}
                accent={settings.primary_color}
              />
            </ImageUpload>
            <label htmlFor="booking-title" className={`${LABEL} mt-4 block`}>
              Title
            </label>
            <input
              id="booking-title"
              type="text"
              maxLength={100}
              value={settings.title}
              onChange={(e) => set({ title: e.target.value })}
              className={`${COMPACT_INPUT} mt-1 w-full`}
            />
            <label htmlFor="booking-about" className={`${LABEL} mt-4 block`}>
              About
            </label>
            <textarea
              id="booking-about"
              rows={4}
              value={settings.bio}
              onChange={(e) => set({ bio: e.target.value })}
              className={`${COMPACT_TEXTAREA} mt-1`}
            />
            <label htmlFor="booking-color" className={`${LABEL} mt-4 block`}>
              Button color
            </label>
            <div className="mt-1 flex items-center gap-2 text-sm">
              <input
                id="booking-color"
                type="color"
                value={settings.primary_color}
                onChange={(e) =>
                  set({ primary_color: e.target.value.toUpperCase() })
                }
                className="h-8 w-8 cursor-pointer rounded border-0 p-0"
              />
              {settings.primary_color}
            </div>
          </section>

          <section className={SECTION}>
            <h3 className={SECTION_TITLE}>Subjects</h3>
            <p className={SECTION_TEXT}>
              Families see a subject only when a tutor teaches it. Add subjects
              in the Subjects tab.
            </p>
            <div className="mt-3 flex flex-col divide-y divide-gray-100">
              {subjects.map((subject) => (
                <div key={subject.id} className="py-2">
                  <Switch
                    name={`show-subject-${subject.id}`}
                    checked={settings.shown_subject_ids.includes(subject.id)}
                    onCheckedChange={(on) =>
                      set({
                        shown_subject_ids: on
                          ? [...settings.shown_subject_ids, subject.id]
                          : settings.shown_subject_ids.filter(
                              (id) => id !== subject.id
                            ),
                      })
                    }
                  >
                    <label
                      htmlFor={`show-subject-${subject.id}`}
                      className="cursor-pointer text-sm"
                    >
                      {subject.name}
                    </label>
                  </Switch>
                </div>
              ))}
            </div>
          </section>
        </div>

        <div className="flex flex-col gap-4">
          <section className={SECTION}>
            <h3 className={SECTION_TITLE}>Session length</h3>
            <p className={SECTION_TEXT}>How long a booked session is.</p>
            <div className="mt-3 flex flex-col gap-2.5">
              <CompactRadio
                id="length-choose"
                name="length"
                checked={settings.families_choose_duration}
                onChange={() =>
                  // The fixed length becomes one of the choices, and the start.
                  set({
                    families_choose_duration: true,
                    duration_options: settings.duration_options.includes(
                      settings.default_duration
                    )
                      ? settings.duration_options
                      : [
                          ...settings.duration_options,
                          settings.default_duration,
                        ].sort((a, b) => a - b),
                  })
                }
              >
                Families choose
              </CompactRadio>
              {settings.families_choose_duration && (
                <div className="ml-6 flex flex-col gap-2">
                  <div className="flex flex-wrap gap-2">
                    {choices.lengths.map((minutes) => {
                      const on = settings.duration_options.includes(minutes);
                      return (
                        <button
                          key={minutes}
                          type="button"
                          aria-pressed={on}
                          onClick={() => toggleLength(minutes)}
                          className={`rounded-full border px-3 py-1 text-sm ${
                            on
                              ? "border-green-300 bg-green-50 font-semibold text-green-700"
                              : "border-gray-300 text-gray-700"
                          }`}
                        >
                          {on && "✓ "}
                          {minutes} min
                        </button>
                      );
                    })}
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-600">
                    Starts on
                    <select
                      value={settings.default_duration}
                      onChange={(e) =>
                        set({ default_duration: Number(e.target.value) })
                      }
                      className={`${COMPACT_INPUT} pr-8`}
                    >
                      {settings.duration_options.map((m) => (
                        <option key={m} value={m}>
                          {m} min
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              )}
              <CompactRadio
                id="length-fixed"
                name="length"
                checked={!settings.families_choose_duration}
                onChange={() => set({ families_choose_duration: false })}
              >
                Always the same length. Not shown to families.
              </CompactRadio>
              {!settings.families_choose_duration && (
                <select
                  aria-label="Session length"
                  value={settings.default_duration}
                  onChange={(e) =>
                    set({ default_duration: Number(e.target.value) })
                  }
                  className={`${COMPACT_INPUT} ml-6 w-32 pr-8`}
                >
                  {Array.from(
                    new Set([...choices.lengths, settings.default_duration])
                  )
                    .sort((a, b) => a - b)
                    .map((m) => (
                      <option key={m} value={m}>
                        {m} min
                      </option>
                    ))}
                </select>
              )}
            </div>
          </section>

          <section className={SECTION}>
            <h3 className={SECTION_TITLE}>Location</h3>
            <p className={SECTION_TEXT}>
              Where a booked session happens. Edit places in the Locations tab.
            </p>
            <div className="mt-3 flex flex-col gap-2.5">
              <CompactRadio
                id="place-choose"
                name="place"
                checked={settings.families_choose_location}
                onChange={() =>
                  places.length &&
                  set({
                    families_choose_location: true,
                    offered_location_ids: settings.offered_location_ids.length
                      ? settings.offered_location_ids
                      : places.map((p) => p.id),
                  })
                }
              >
                Families choose
              </CompactRadio>
              {settings.families_choose_location && (
                <div className="ml-6 flex flex-col gap-2">
                  {places.map((place) => (
                    <span
                      key={place.id}
                      className="inline-flex items-center gap-2 text-sm"
                    >
                      <input
                        id={`offer-place-${place.id}`}
                        type="checkbox"
                        className={`${COMPACT_CHECK} rounded`}
                        checked={settings.offered_location_ids.includes(
                          place.id
                        )}
                        onChange={() => togglePlace(place.id)}
                      />
                      <label
                        htmlFor={`offer-place-${place.id}`}
                        className="cursor-pointer"
                      >
                        {place.name}{" "}
                        <span className="text-xs text-gray-400">
                          {place.address_line_1 ??
                            (place.location_type === "ONLINE" ? "Online" : "")}
                        </span>
                      </label>
                    </span>
                  ))}
                </div>
              )}
              <CompactRadio
                id="place-fixed"
                name="place"
                checked={!settings.families_choose_location}
                onChange={() => set({ families_choose_location: false })}
              >
                Always the same place. Not shown to families.
              </CompactRadio>
              {!settings.families_choose_location && (
                <select
                  aria-label="Place"
                  value={settings.default_location_id ?? ""}
                  onChange={(e) =>
                    set({
                      default_location_id: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                  className={`${COMPACT_INPUT} ml-6 max-w-xs pr-8`}
                >
                  <option value="">Tutor will confirm</option>
                  {places.map((place) => (
                    <option key={place.id} value={place.id}>
                      {place.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </section>

          <section className={SECTION}>
            <h3 className={SECTION_TITLE}>When families can book</h3>
            <div className="mt-3 grid gap-3 sm:grid-cols-3">
              <label className="flex flex-col gap-1">
                <span className={LABEL}>Minimum notice</span>
                <select
                  value={settings.min_notice_minutes}
                  onChange={(e) =>
                    set({ min_notice_minutes: Number(e.target.value) })
                  }
                  className={`${COMPACT_INPUT} pr-8`}
                >
                  {choices.min_notice_minutes.map((m) => (
                    <option key={m} value={m}>
                      {noticeLabel(m)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className={LABEL}>How far ahead</span>
                <select
                  value={settings.max_days_ahead ?? ""}
                  onChange={(e) =>
                    set({
                      max_days_ahead: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                  className={`${COMPACT_INPUT} pr-8`}
                >
                  <option value="">No limit</option>
                  {choices.max_days_ahead.map((d) => (
                    <option key={d} value={d}>
                      {windowLabel(d)}
                    </option>
                  ))}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className={LABEL}>Free time between sessions</span>
                <select
                  value={settings.buffer_minutes}
                  onChange={(e) =>
                    set({ buffer_minutes: Number(e.target.value) })
                  }
                  className={`${COMPACT_INPUT} pr-8`}
                >
                  {choices.buffer_minutes.map((m) => (
                    <option key={m} value={m}>
                      {m === 0 ? "None" : `${m} min`}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <p className={`${SECTION_TEXT} text-xs`}>
              Free time is kept before and after each session and calendar
              event.
            </p>
          </section>

          <section className={SECTION}>
            <h3 className={SECTION_TITLE}>Who can book</h3>
            <p className={SECTION_TEXT}>
              Families prove who they are with a code texted to their phone.
            </p>
            <div className="mt-3 flex flex-col gap-2.5">
              <CompactRadio
                id="who-anyone"
                name="who"
                checked={!settings.returning_families_only}
                onChange={() => set({ returning_families_only: false })}
              >
                New and returning families
              </CompactRadio>
              <CompactRadio
                id="who-returning"
                name="who"
                checked={settings.returning_families_only}
                onChange={() => set({ returning_families_only: true })}
              >
                Returning families only{" "}
                <span className="text-xs text-gray-400">
                  (students already in Tadpole)
                </span>
              </CompactRadio>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
